import { useCallback, useEffect, useMemo, useState } from 'react'
import { useUserRole } from '@/hooks/useUserRole'
import { getAuthToken } from '@/lib/api/apiClient'
import { DEFAULT_STATUSES, RENTAL_STATUSES } from '../constants/residentConstants'
import {
  buildResidentExportUrl,
  fetchCompanies,
  fetchProperties,
  fetchResidentList,
  fetchSalesUsers,
} from '../services/residentApi'
import type { ResidentListFilters, ResidentListQuery, ResidentListRow, SelectOption } from '../types'
import {
  clearResidentListSessionFilter,
  readResidentListSessionFilter,
  writeResidentListSessionFilter,
} from '../utils/residentSessionFilter'
import { buildInitialResidentListQuery, buildResidentListQuery } from '../utils/residentQuery'

export function useResidentList() {
  const role = useUserRole()
  const [query, setQuery] = useState<ResidentListQuery>(() =>
    buildInitialResidentListQuery(role, readResidentListSessionFilter()),
  )
  const [rows, setRows] = useState<ResidentListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({ current: 1, last: 1, total: 0 })
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>(() =>
    query.companyId && query.company_name ? [{ value: query.companyId, label: query.company_name }] : [],
  )
  const [propertyOptions, setPropertyOptions] = useState<SelectOption[]>(() =>
    query.propertyId && query.property_name ? [{ value: query.propertyId, label: query.property_name }] : [],
  )
  const [salesOptions, setSalesOptions] = useState<SelectOption[]>([])

  const load = useCallback(async (nextQuery: ResidentListQuery) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetchResidentList(nextQuery)
      setRows(response.data || [])
      setPagination({
        current: response.current ?? nextQuery.page ?? 1,
        last: response.last ?? 1,
        total: response.total ?? 0,
      })
    } catch {
      // Angular `loadResidents` has no error handler — failed/empty searches show
      // emptydata ("No Records Found…"), not an inline failure banner.
      setRows([])
      setPagination({ current: 1, last: 1, total: 0 })
      setError(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const persistAndLoad = useCallback(
    (next: ResidentListQuery) => {
      setQuery(next)
      writeResidentListSessionFilter(next)
      void load(next)
    },
    [load],
  )

  const applyFilters = useCallback(
    (filters: Partial<ResidentListFilters>) => {
      const next = buildResidentListQuery(query, filters)
      persistAndLoad(next)
    },
    [persistAndLoad, query],
  )

  const resetFilters = useCallback(() => {
    clearResidentListSessionFilter()
    const next = buildInitialResidentListQuery(role)
    setQuery(next)
    writeResidentListSessionFilter(next)
    void load(next)
  }, [load, role])

  const changePage = useCallback(
    (page: number) => {
      persistAndLoad({ ...query, page })
    },
    [persistAndLoad, query],
  )

  const changePerPage = useCallback(
    (perPage: number) => {
      persistAndLoad({ ...query, page: 1, perPage })
    },
    [persistAndLoad, query],
  )

  /** Matches Angular `resident-list.sort()`: each click toggles asc/desc, then applies `orderBy`. */
  const sort = useCallback(
    (orderBy: string) => {
      persistAndLoad({
        ...query,
        page: 1,
        orderBy,
        sortBy: query.sortBy === 'asc' ? 'desc' : 'asc',
      })
    },
    [persistAndLoad, query],
  )

  const refetch = useCallback(() => {
    void load(query)
  }, [load, query])

  /** Angular `triggerFilter()`: re-applies the current filters but forces `page: 1`. */
  const refetchFromStart = useCallback(() => {
    persistAndLoad({ ...query, page: 1 })
  }, [persistAndLoad, query])

  const searchCompanies = useCallback(async (search?: string, isPaste = false) => {
    // Angular `searchCompany`: only query when the typed text length >= 2 (bypassed on paste).
    if (!isPaste && typeof search === 'string' && search.length < 2) return [] as SelectOption[]
    const options = await fetchCompanies(search)
    setCompanyOptions(options)
    return options
  }, [])

  const searchProperties = useCallback(
    async (search?: string, overrideCompanyId?: string | number, isPaste = false) => {
      // Angular `searchProperty`: only query when the typed text length is even (bypassed on paste).
      if (!isPaste && typeof search === 'string' && search.length % 2 !== 0) return [] as SelectOption[]
      const rawCompanyId = overrideCompanyId ?? query.companyId
      const companyToUse = rawCompanyId !== undefined && rawCompanyId !== null ? String(rawCompanyId) : undefined
      const options = await fetchProperties(search, companyToUse)
      setPropertyOptions(options)
      return options
    },
    [query.companyId],
  )

  /**
   * Angular `triggerFilter` on first load: merge `residentlist_filter` from session, then load once.
   * Re-read session here (not only in useState) so application-detail handoff is never missed.
   * Also read URL `?applicant_id=` because `window.open(..., 'noopener')` does NOT share sessionStorage
   * with the source tab; the URL param is the only reliable handoff for the Resident button.
   */
  useEffect(() => {
    const sessionFilter = readResidentListSessionFilter()
    let merged: typeof sessionFilter = sessionFilter
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search)
      const applicantIdFromUrl = searchParams.get('applicant_id')?.trim()
      const pastCustomerFromUrl = searchParams.get('past_customer')?.trim()
      if (applicantIdFromUrl || pastCustomerFromUrl) {
        // Angular `routeParams` patches `q`; application handoff also sets past_customer=1.
        merged = {
          ...sessionFilter,
          ...(applicantIdFromUrl ? { q: applicantIdFromUrl } : {}),
          ...(pastCustomerFromUrl ? { past_customer: pastCustomerFromUrl } : {}),
        }
      }
    }
    const next = buildInitialResidentListQuery(role, merged)
    setQuery(next)
    writeResidentListSessionFilter(next)
    void load(next)
    void fetchSalesUsers().then(setSalesOptions).catch(() => setSalesOptions([]))
    if (!role.companyId) void searchCompanies()
    if (!role.propertyId) void searchProperties()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount bootstrap only
  }, [])

  const rentalStatusOptions = useMemo(
    () => Object.entries(RENTAL_STATUSES).map(([value, label]) => ({ value, label })),
    [],
  )
  const defaultStatusOptions = useMemo(
    () => Object.entries(DEFAULT_STATUSES).map(([value, label]) => ({ value, label })),
    [],
  )

  const openExportInNewTab = useCallback(() => {
    const url = buildResidentExportUrl(query, getAuthToken())
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [query])

  return {
    rows,
    loading,
    error,
    query,
    pagination,
    companyOptions,
    propertyOptions,
    salesOptions,
    rentalStatusOptions,
    defaultStatusOptions,
    isCompanyLocked: Boolean(role.companyId),
    isPropertyLocked: Boolean(role.propertyId),
    openExportInNewTab,
    applyFilters,
    resetFilters,
    changePage,
    changePerPage,
    sort,
    refetch,
    refetchFromStart,
    searchCompanies,
    searchProperties,
  }
}
